import { useFuzzySearchList } from '@nozbe/microfuzz/react';
import { isNotNil } from 'rambda';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import { Virtuoso } from 'react-virtuoso';
import { useDebounceValue } from 'usehooks-ts';
import SearchInput from '@/components/common/search-input';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import SearchListItem from '@/components/search/search-list-item';
import TagHeaderMenu from '@/components/tags/tag-header-menu';
import useRemoveTagMember from '@/hooks/tags/use-remove-tag-member';
import useTagMembers from '@/hooks/tags/use-tag-members';
import { useTagById } from '@/hooks/tags/use-tags';
import type { TagMember } from '@/models/user/tag';
import ROUTES from '@/routes';
import { makePrompt } from '@/stores/prompter';

export default function TagById() {
  const { tagId } = useTypedParams(ROUTES.CONTACT.TAGS.BY_ID);
  const { data: currentTag } = useTagById(tagId);
  const { tagMembers } = useTagMembers(tagId);
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState<string>('');
  const [debouncedKeyword] = useDebounceValue(keyword, 300);
  const { mutateAsync: removeTagMember } = useRemoveTagMember(tagId);

  const filteredMembers = useFuzzySearchList({
    list: tagMembers,
    queryText: debouncedKeyword,
    getText: (item) => [item.NickName].filter(isNotNil),
    mapResultItem: ({ item }) => item,
  });

  const handleRemoveTagMember = (member: TagMember) =>
    makePrompt({
      key: 'remove-tag-member',
      title: t('contact.tags.remove-tag-member.title'),
      content: t('contact.tags.remove-tag-member.confirm-message', { nickName: member.NickName }),
      promise: () => removeTagMember(member.MemberId),
    });

  return (
    <PageLayout
      title={currentTag ? `${currentTag.TagName}(${currentTag.TagCount})` : t('contact.tags.title')}
      headerConfig={{
        actions: <TagHeaderMenu />,
        navigateBack: true,
        elementAfterNav: (
          <SearchInput
            className='mx-5'
            keyword={keyword}
            setKeyword={setKeyword}
            clearKeyword={() => setKeyword('')}
          />
        ),
      }}>
      <Virtuoso
        className='flex flex-1 flex-col items-stretch'
        data={filteredMembers}
        itemContent={(_, member) => (
          <SearchListItem
            avatarImageUrl={member.HeadImg}
            title={member.NickName}
            highlight={keyword}
            suffix={
              <button
                type='button'
                className='btn btn-primary btn-sm'
                onClick={() => handleRemoveTagMember(member)}>
                {t('common.remove')}
              </button>
            }
          />
        )}
      />

      <Link
        to={ROUTES.CONTACT.TAGS.BY_ID.$.ADD_MEMBER.$path({ relative: true })}
        className='btn btn-primary btn-md mx-5'>
        {t('common.add')}
      </Link>
    </PageLayout>
  );
}
